// src/services/activityApi.js
// API для работы с логом активности пользователей

import { API_URL } from '../config';

/**
 * Получение токена из localStorage
 */
const getToken = () => {
  return localStorage.getItem('token');
};

/**
 * Базовые заголовки для запросов
 */
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`,
});

/**
 * Обработка ошибок API
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Ошибка сети' }));
    throw new Error(error.detail || `Ошибка ${response.status}`);
  }
  return response.json();
};

/**
 * Получение лога активности
 * @param {Object} params - Параметры фильтрации
 * @param {number} params.user_id - Фильтр по пользователю
 * @param {string} params.user_query - Поиск по имени пользователя
 * @param {string} params.action - Фильтр по действию
 * @param {string} params.entity_type - Фильтр по типу сущности
 * @param {number} params.entity_id - Фильтр по ID сущности
 * @param {string} params.category - Фильтр по категории
 * @param {string} params.query - Общий поиск
 * @param {string} params.date_from - Дата от (YYYY-MM-DD)
 * @param {string} params.date_to - Дата до (YYYY-MM-DD)
 * @param {number} params.limit - Лимит записей
 * @param {number} params.offset - Смещение
 */
export const getActivityLogs = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.user_id) queryParams.append('user_id', params.user_id);
  if (params.user_query) queryParams.append('user_query', params.user_query);
  if (params.action) queryParams.append('action', params.action);
  if (params.entity_type) queryParams.append('entity_type', params.entity_type);
  if (params.entity_id) queryParams.append('entity_id', params.entity_id);
  if (params.order_id) queryParams.append('order_id', params.order_id);
  if (params.category) queryParams.append('category', params.category);
  if (params.query) queryParams.append('query', params.query);
  if (params.date_from) queryParams.append('date_from', params.date_from);
  if (params.date_to) queryParams.append('date_to', params.date_to);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);

  const response = await fetch(`${API_URL}/api/activity-log?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение статистики активности
 * @param {Object} params - Параметры фильтрации
 * @param {number} params.user_id - Фильтр по пользователю
 * @param {string} params.user_query - Поиск по имени пользователя
 * @param {string} params.category - Фильтр по категории
 * @param {string} params.query - Общий поиск
 * @param {string} params.date_from - Дата от (YYYY-MM-DD)
 * @param {string} params.date_to - Дата до (YYYY-MM-DD)
 */
export const getActivityStats = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.user_id) queryParams.append('user_id', params.user_id);
  if (params.user_query) queryParams.append('user_query', params.user_query);
  if (params.action) queryParams.append('action', params.action);
  if (params.entity_type) queryParams.append('entity_type', params.entity_type);
  if (params.entity_id) queryParams.append('entity_id', params.entity_id);
  if (params.order_id) queryParams.append('order_id', params.order_id);
  if (params.category) queryParams.append('category', params.category);
  if (params.query) queryParams.append('query', params.query);
  if (params.date_from) queryParams.append('date_from', params.date_from);
  if (params.date_to) queryParams.append('date_to', params.date_to);

  const response = await fetch(`${API_URL}/api/activity-log/stats?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Метаданные для фильтров активности
 * @param {Object} params - Параметры фильтрации
 */
export const getActivityMeta = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.user_query) queryParams.append('user_query', params.user_query);
  if (params.category) queryParams.append('category', params.category);
  if (params.query) queryParams.append('query', params.query);
  if (params.date_from) queryParams.append('date_from', params.date_from);
  if (params.date_to) queryParams.append('date_to', params.date_to);

  const response = await fetch(`${API_URL}/api/activity-log/meta?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение активности конкретного пользователя
 * @param {number} userId - ID пользователя
 * @param {number} limit - Лимит записей
 * @param {number} offset - Смещение
 */
export const getUserActivity = async (userId, limit = 100, offset = 0) => {
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit);
  queryParams.append('offset', offset);

  const response = await fetch(`${API_URL}/api/activity-log/user/${userId}?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение истории изменений конкретного пользователя
 * @param {number} userId - ID пользователя
 * @param {number} limit - Лимит записей
 * @param {number} offset - Смещение
 */
export const getUserChangeHistory = async (userId, limit = 50, offset = 0) => {
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit);
  queryParams.append('offset', offset);

  const response = await fetch(`${API_URL}/api/activity-log/target/user/${userId}?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение истории изменений конкретного подразделения
 * @param {number} departmentId - ID подразделения
 * @param {number} limit - Лимит записей
 * @param {number} offset - Смещение
 */
export const getDepartmentChangeHistory = async (departmentId, limit = 50, offset = 0) => {
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit);
  queryParams.append('offset', offset);

  const response = await fetch(`${API_URL}/api/activity-log/target/department/${departmentId}?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение истории изменений конкретной скидки
 * @param {number} templateId - ID скидки
 * @param {number} limit - Лимит записей
 * @param {number} offset - Смещение
 */
export const getDiscountTemplateChangeHistory = async (templateId, limit = 50, offset = 0) => {
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit);
  queryParams.append('offset', offset);

  const response = await fetch(`${API_URL}/api/activity-log/target/discount-template/${templateId}?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение истории изменений конкретной акции
 * @param {number} promotionId - ID акции
 * @param {number} limit - Лимит записей
 * @param {number} offset - Смещение
 */
export const getPromotionChangeHistory = async (promotionId, limit = 50, offset = 0) => {
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit);
  queryParams.append('offset', offset);

  const response = await fetch(`${API_URL}/api/activity-log/target/promotion/${promotionId}?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

const activityApi = {
  getActivityLogs,
  getActivityStats,
  getActivityMeta,
  getUserActivity,
  getUserChangeHistory,
  getDepartmentChangeHistory,
  getDiscountTemplateChangeHistory,
  getPromotionChangeHistory,
};

export default activityApi;
